/** MyDesk — the freelancer's home surface.
 *
 *  Route: /me
 *
 *  Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  HERO — name · headline · avatar · verify · visibility     │
 *   ├────────────────────────────────────────────────────────────┤
 *   │  INCOMING INVITATIONS — pending rows w/ Accept · Decline   │
 *   ├────────────────────────────────────────────────────────────┤
 *   │  ACTIVE PROJECTS DESK — cubicle tile grid                  │
 *   └────────────────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  BadgeCheck, MapPin, Briefcase, Users, Eye, EyeOff, Radar,
  Check, X, Sparkles, Clock, Film, Camera, Palette, Building2, Mail,
} from "lucide-react";

const VISIBILITY_MODES = [
  { value: "public",       label: "Public",         icon: Eye,   copy: "Discoverable across the People Directory." },
  { value: "project_only", label: "Project only",   icon: Radar, copy: "Hidden from casual search — only surfaces when a Producer/CD searches Project Directory by role." },
  { value: "hidden",       label: "Hidden",         icon: EyeOff,copy: "Removed from all discovery surfaces. You can still be invited by direct message or via a project." },
];

const CUBICLE_ICON = {
  casting:   Users,
  costume:   Palette,
  camera:    Camera,
  art:       Palette,
  direction: Film,
  production: Briefcase,
};

export default function MyDesk() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [desk, setDesk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/me/desk");
      setDesk(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load desk");
    }
  };

  useEffect(() => { void load(); }, []);

  if (!desk) {
    return <div className="p-10 text-white/50 text-sm" data-testid="my-desk-loading">Loading desk…</div>;
  }

  const { profile, pending_invitations = [], cubicles = [], counters = {} } = desk;

  const setVisibility = async (mode) => {
    setBusy(true);
    try {
      await api.patch("/me/desk/visibility", { directory_visibility: mode });
      setDesk({ ...desk, profile: { ...profile, directory_visibility: mode } });
      toast.success(`Visibility → ${mode.replace("_", " ")}`);
      setPickerOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const openCubicle = (tile) => {
    // Deep-link the cubicle inside the Virtual Production Office.
    // Sub-members (scope=cubicle_only) get a scope query-param so the
    // VPO can hide other cubicle tabs and the project overview.
    const parts = [];
    if (tile.cubicle && tile.cubicle !== "office")
      parts.push(`cubicle=${encodeURIComponent(tile.cubicle)}`);
    if (tile.scope === "cubicle_only")
      parts.push("scope=cubicle_only");
    const q = parts.length ? `?${parts.join("&")}` : "";
    nav(`/projects/${tile.project_id}/cubicles${q}`);
  };

  const acceptInvite = async (row) => {
    setBusy(true);
    try {
      // Cubicle-only invites go through a different endpoint.
      const url = row.scope === "cubicle_only"
        ? `/cubicle-invites/${row.row_id}/accept`
        : `/invitations/${row.row_id}/accept`;
      await api.post(url, {});
      toast.success(`Joined ${row.title}. Your cubicle is now open.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const openDecline = (row) => { setDeclineFor(row); setDeclineReason(""); };
  const confirmDecline = async () => {
    if (!declineFor) return;
    setBusy(true);
    try {
      const url = declineFor.scope === "cubicle_only"
        ? `/cubicle-invites/${declineFor.row_id}/decline`
        : `/invitations/${declineFor.row_id}/decline`;
      await api.post(url, { reason: declineReason });
      toast.success("Invitation declined.");
      setDeclineFor(null); setDeclineReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] pb-16" data-testid="my-desk-page">
      {/* ─────────────────────── HERO ─────────────────────── */}
      <HeroCard profile={profile} counters={counters}
                onOpenPicker={() => setPickerOpen(!pickerOpen)}
                pickerOpen={pickerOpen}
                onSetVisibility={setVisibility}
                busy={busy}/>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">

        {/* ─────────────────────── INCOMING INVITES ─────────────────────── */}
        {pending_invitations.length > 0 && (
          <section className="mt-10" data-testid="incoming-invitations-section">
            <SectionHeader
              eyebrow="Incoming"
              title="Invitations & discussions"
              count={pending_invitations.length}/>
            <div className="mt-5 space-y-3">
              {pending_invitations.map((row) => (
                <InvitationRow
                  key={row.row_id}
                  row={row}
                  onAccept={() => acceptInvite(row)}
                  onDecline={() => openDecline(row)}
                  busy={busy}/>
              ))}
            </div>
          </section>
        )}

        {/* ─────────────────────── ACTIVE PROJECTS DESK ─────────────────── */}
        <section className="mt-14" data-testid="active-projects-desk-section">
          <SectionHeader
            eyebrow="On your desk"
            title="Active cubicles"
            count={cubicles.length}/>
          {cubicles.length === 0 ? (
            <EmptyDesk/>
          ) : (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                 data-testid="active-cubicles-grid">
              {cubicles.map((tile) => (
                <CubicleTile key={`${tile.project_id}-${tile.row_id || 'own'}`}
                             tile={tile}
                             onOpen={() => openCubicle(tile)}/>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* Decline modal */}
      {declineFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setDeclineFor(null)}
             data-testid="decline-invitation-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-neutral-950 border border-white/12 rounded-2xl p-6 max-w-md w-full">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Decline invite</div>
            <div className="font-display text-2xl mt-2">{declineFor.title}</div>
            <div className="text-xs text-white/60 mt-1">
              Role: {declineFor.role_label || declineFor.role}
            </div>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason (optional — helps the PH schedule around you)…"
              className="mt-4 w-full h-24 bg-black/50 border border-white/15 rounded-lg p-3 text-sm text-white/85"
              data-testid="decline-reason-input"/>
            <div className="mt-4 flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setDeclineFor(null)}
                      className="text-white/60"
                      data-testid="decline-cancel-btn">Cancel</Button>
              <Button onClick={confirmDecline} disabled={busy}
                      className="bg-red-500/80 hover:bg-red-500 text-white rounded-full px-6"
                      data-testid="decline-confirm-btn">
                Decline invitation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────
function HeroCard({ profile, counters, onOpenPicker, pickerOpen,
                    onSetVisibility, busy }) {
  const current = VISIBILITY_MODES.find((v) => v.value === profile.directory_visibility)
                  || VISIBILITY_MODES[0];
  const VIcon = current.icon;
  return (
    <div className="relative overflow-hidden border-b border-white/10">
      {/* subtle grain */}
      <div className="absolute inset-0 opacity-25 pointer-events-none"
           style={{background: "radial-gradient(1200px 400px at 20% -10%, rgba(255,120,180,0.14), transparent 60%), radial-gradient(900px 300px at 80% 0%, rgba(120,180,255,0.10), transparent 60%)"}}/>
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row gap-6 items-start">
        <div className="flex items-center gap-5 flex-1 min-w-0">
          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/8 flex items-center justify-center overflow-hidden ring-1 ring-white/12"
               data-testid="hero-avatar">
            {profile.avatar_url
              ? <img src={profile.avatar_url} className="w-full h-full object-cover" alt=""/>
              : <span className="font-display text-3xl text-white/70">{(profile.name || "?").slice(0,1)}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">Signed in as</div>
            <h1 className="font-display text-3xl md:text-4xl truncate" data-testid="hero-name">
              {profile.name || "You"}
              {profile.verified && <BadgeCheck size={20} className="inline ml-2 -mt-1 text-emerald-400"/>}
            </h1>
            <div className="mt-1.5 text-sm text-white/70 flex items-center gap-3 flex-wrap">
              {profile.headline && <span>{profile.headline}</span>}
              {profile.primary_role && (
                <span className="inline-flex items-center gap-1 text-white/60">
                  <Briefcase size={12}/> {profile.primary_role}
                </span>
              )}
              {profile.city && (
                <span className="inline-flex items-center gap-1 text-white/60">
                  <MapPin size={12}/> {profile.city}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-col md:items-end gap-3">
          {/* Counters */}
          <div className="flex gap-2 flex-wrap" data-testid="hero-counters">
            <Counter label="Pending" value={counters.pending || 0} accent="amber"/>
            <Counter label="Active"  value={counters.active  || 0} accent="emerald"/>
            <Counter label="Owned"   value={counters.owned   || 0} accent="sky"/>
          </div>

          {/* Visibility picker */}
          <div className="relative">
            <button
              type="button"
              onClick={onOpenPicker}
              className="flex items-center gap-2 h-9 px-3 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white text-[11px] uppercase tracking-[0.22em]"
              data-testid="visibility-picker-trigger"
              disabled={busy}
            >
              <VIcon size={13}/>
              <span>Visibility · {current.label}</span>
            </button>
            {pickerOpen && (
              <div className="absolute right-0 top-full mt-2 z-40 w-80 rounded-xl border border-white/15 bg-neutral-950/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                   data-testid="visibility-picker-menu">
                {VISIBILITY_MODES.map((v) => {
                  const Icon = v.icon;
                  const active = v.value === profile.directory_visibility;
                  return (
                    <button key={v.value} type="button"
                            onClick={() => onSetVisibility(v.value)}
                            disabled={busy}
                            className={"w-full text-left px-4 py-3 hover:bg-white/[0.06] flex items-start gap-3 border-b border-white/8 last:border-b-0 "
                                     + (active ? "bg-white/[0.04]" : "")}
                            data-testid={`visibility-option-${v.value}`}>
                      <Icon size={15} className="text-white/80 mt-0.5"/>
                      <div className="flex-1">
                        <div className="text-sm font-display flex items-center gap-2">
                          {v.label} {active && <Check size={12} className="text-emerald-400"/>}
                        </div>
                        <div className="text-[11px] text-white/55 mt-1 leading-snug">{v.copy}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Counter({ label, value, accent }) {
  const map = {
    amber:   "text-amber-300",
    emerald: "text-emerald-300",
    sky:     "text-sky-300",
  };
  return (
    <div className="cs-card rounded-xl px-3 py-2 flex items-center gap-2"
         data-testid={`counter-${label.toLowerCase()}`}>
      <div className={`font-display text-2xl leading-none ${map[accent] || "text-white"}`}>{value}</div>
      <div className="text-[9px] tracking-[0.22em] uppercase text-white/50 pl-0.5">{label}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Section header
// ─────────────────────────────────────────────────────────────────────
function SectionHeader({ eyebrow, title, count }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{eyebrow}</div>
        <h2 className="font-display text-2xl mt-1">{title}</h2>
      </div>
      {typeof count === "number" && (
        <div className="text-[10px] tracking-[0.24em] uppercase text-white/45">{count} total</div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Invitation row
// ─────────────────────────────────────────────────────────────────────
function InvitationRow({ row, onAccept, onDecline, busy }) {
  return (
    <div className="cs-card rounded-2xl p-5 flex flex-col md:flex-row gap-4 items-start md:items-center"
         data-testid={`invitation-row-${row.row_id}`}>
      <div className="w-14 h-14 rounded-xl bg-white/[0.06] flex items-center justify-center overflow-hidden shrink-0">
        {row.poster_url
          ? <img src={row.poster_url} className="w-full h-full object-cover" alt=""/>
          : <Film size={22} className="text-white/50"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300/85 flex items-center gap-2">
          <span>Invitation · {row.status}</span>
          {row.scope === "cubicle_only" && (
            <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-white/80 text-[9px] tracking-[0.2em]"
                  data-testid={`invitation-scope-cubicle-${row.row_id}`}>
              CUBICLE ONLY · {row.cubicle}
            </span>
          )}
        </div>
        <div className="font-display text-lg mt-1 truncate">{row.title}</div>
        <div className="text-xs text-white/60 mt-1 flex items-center gap-3 flex-wrap">
          {row.ph_name && <span className="inline-flex items-center gap-1"><Building2 size={11}/> {row.ph_name}</span>}
          {row.role_label && <span>· Role: <span className="text-white/80">{row.role_label}</span></span>}
          {row.inviter_name && <span>· By: {row.inviter_name}</span>}
        </div>
        {row.brief && (
          <div className="mt-2 text-[13px] text-white/65 max-w-2xl leading-relaxed line-clamp-3">
            {row.brief}
          </div>
        )}
        {(row.start_date || row.location) && (
          <div className="mt-2 text-[11px] text-white/50 flex items-center gap-3 flex-wrap">
            {row.start_date && <span className="inline-flex items-center gap-1"><Clock size={10}/> {row.start_date}{row.end_date ? ` → ${row.end_date}` : ""}</span>}
            {row.location && <span className="inline-flex items-center gap-1"><MapPin size={10}/> {row.location}</span>}
          </div>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        {row.scope === "cubicle_only" && (
          <Button
            onClick={() => window.location.assign(`/cubicle-invites/${row.row_id}/preview`)}
            disabled={busy}
            variant="ghost"
            className="border border-white/15 h-10 px-4 rounded-full text-white/75 hover:text-white uppercase tracking-[0.2em] text-[11px]"
            data-testid={`preview-invitation-${row.row_id}`}>
            <Eye size={14}/> Preview
          </Button>
        )}
        <Button onClick={onAccept} disabled={busy}
                className="btn-themed h-10 px-5 rounded-full uppercase tracking-[0.2em] text-[11px]"
                data-testid={`accept-invitation-${row.row_id}`}>
          <Check size={14}/> {row.scope === "cubicle_only" ? "Enter" : "Accept"}
        </Button>
        <Button onClick={onDecline} disabled={busy} variant="ghost"
                className="border border-white/15 h-10 px-5 rounded-full text-white/75 hover:text-red-300 uppercase tracking-[0.2em] text-[11px]"
                data-testid={`decline-invitation-${row.row_id}`}>
          <X size={14}/> Decline
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Cubicle tile
// ─────────────────────────────────────────────────────────────────────
function CubicleTile({ tile, onOpen }) {
  const Icon = CUBICLE_ICON[tile.cubicle] || Briefcase;
  const statusColor = tile.is_owner ? "text-sky-300"
    : tile.status === "accepted" || tile.status === "finalized" ? "text-emerald-300"
    : tile.status === "discussion" ? "text-amber-300" : "text-white/60";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="cs-card rounded-2xl p-5 text-left hover:border-white/25 transition group relative overflow-hidden"
      data-testid={`cubicle-tile-${tile.project_id}${tile.row_id ? '-' + tile.row_id : ''}`}
    >
      {/* poster wash */}
      {tile.poster_url && (
        <div className="absolute inset-0 opacity-25 pointer-events-none"
             style={{backgroundImage:`url(${tile.poster_url})`,backgroundSize:"cover",backgroundPosition:"center"}}/>
      )}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white/[0.08] flex items-center justify-center">
            <Icon size={16} className="text-white/85"/>
          </div>
          <div className="text-[9px] tracking-[0.28em] uppercase text-white/45">
            {tile.type_label || tile.project_type || "Project"}
          </div>
        </div>
        <div className="font-display text-lg mt-3 leading-snug line-clamp-2">{tile.title}</div>
        {tile.ph_name && (
          <div className="text-[11px] text-white/55 mt-1 truncate">{tile.ph_name}</div>
        )}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] tracking-[0.22em] uppercase ${statusColor}`}>
            {tile.is_owner ? "You own this" : (tile.status || "active")}
          </span>
          {tile.role_label && !tile.is_owner && (
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/60">
              · {tile.role_label}
            </span>
          )}
          {tile.scope === "cubicle_only" && (
            <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-white/85 text-[9px] tracking-[0.2em]"
                  data-testid={`tile-scope-cubicle-${tile.project_id}-${tile.row_id}`}>
              {tile.cubicle} cubicle only
            </span>
          )}
        </div>
        <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-white/55 flex items-center gap-1 group-hover:text-white">
          Open cubicle <Sparkles size={11}/>
        </div>
      </div>
    </button>
  );
}


function EmptyDesk() {
  return (
    <div className="mt-6 border border-dashed border-white/12 rounded-2xl p-10 text-center"
         data-testid="empty-desk">
      <Briefcase size={22} className="text-white/40 mx-auto"/>
      <div className="font-display text-xl mt-3">Your desk is clear.</div>
      <div className="text-xs text-white/50 mt-2 max-w-md mx-auto">
        Cubicles appear here the moment a Producer invites you into a
        project. Make sure your visibility is set to <em>Public</em> or
        <em> Project-Only</em> so Casting Directors can find you.
      </div>
    </div>
  );
}
